/**
 * Level crossings with barriers, as Swedish roads have them: on each
 * approach, on the right, a post with the white-and-red cross sign and two
 * red lamps, and a striped boom over the lane into the crossing.
 *
 * Each vector chunk's crossings are found from its road and rail ribbons
 * when it comes in (`core/rail/crossings.ts`) and stood in the chunk. As a
 * train comes the lamps flash in turn and the bell rings; the booms come
 * down; the cars stop at them (each lowered boom is a standing `Mover` over
 * its lane) and go on once the train has gone and the booms are up. The
 * bell is heard from the nearest few.
 */

import type { Object3D } from 'three';
import { BoxGeometry, Color, Group, Mesh, MeshStandardMaterial } from 'three';
import type { Crossing, CrossingState } from '../core/rail/crossings.js';
import { barring, coming, findCrossings, openCrossing, signalling, stepCrossing } from '../core/rail/crossings.js';
import { ROAD_STYLE } from '../core/sources/shortbread.js';
import type { SoundSystem } from './audio/system.js';
import type { Mover } from './traffic.js';

/** A train as the crossings watch it: its front, the way it goes, its speed, and its cars. */
export interface TrainView {
  x: number;
  z: number;
  dx: number;
  dz: number;
  speed: number;
  cars: ReadonlyArray<{ x: number; z: number; half: number }>;
}

interface Placed {
  crossing: Crossing;
  state: CrossingState;
  /** Each approach's boom, raised about its pivot, and its lamps. */
  booms: Group[];
  lamps: [MeshStandardMaterial, MeshStandardMaterial];
  /** Seconds to the next ring of its bell. */
  ring: number;
}

interface ChunkCrossings {
  group: Object3D;
  crossings: Placed[];
  /** What was stood in the group, to take away again. */
  objects: Object3D[];
}

/** How far out from the tracks the barriers stand, metres. */
const SET_BACK = 3;
/** Booms up, radians from level. */
const UP = 1.45;
/** How often the bell rings, seconds; heard from the nearest few within this, metres. */
const RING_EVERY = 0.48;
const BELLS = 3;
const BELL_HEARD = 300;
/** How fast the lamps take turns, Hz. */
const FLASH = 1.1;

const RED = new Color(0xff2a1a);
const geometry = {
  post: new BoxGeometry(0.14, 2.7, 0.14),
  head: new BoxGeometry(0.5, 0.26, 0.16),
  lamp: new BoxGeometry(0.2, 0.2, 0.06),
  bar: new BoxGeometry(1.15, 0.16, 0.035),
  boomPiece: new BoxGeometry(1, 0.1, 0.1),
  counter: new BoxGeometry(0.5, 0.3, 0.3),
  box: new BoxGeometry(0.6, 1.1, 0.45),
};
const grey = new MeshStandardMaterial({ color: 0x8c9196, roughness: 0.6, metalness: 0.4, flatShading: true });
const dark = new MeshStandardMaterial({ color: 0x2b2e32, roughness: 0.7, flatShading: true });
const white = new MeshStandardMaterial({ color: 0xf2f2ee, roughness: 0.5, flatShading: true });
const red = new MeshStandardMaterial({ color: 0xc92a24, roughness: 0.5, flatShading: true });

export class RailCrossings {
  private readonly chunks = new Map<string, ChunkCrossings>();
  private clock = 0;

  /** A chunk's crossings, from its placed road ribbons (x, y, z positions, two vertices a cross-section), stood in `group`. */
  add(id: string, group: Object3D, positions: Float32Array, indices: Uint32Array, attributes: Float32Array): void {
    this.remove(id);
    const rails = centrelines(positions, indices, attributes, s => s === ROAD_STYLE.rail);
    if (rails.length === 0) return;
    const roads = centrelines(positions, indices, attributes, s => s <= ROAD_STYLE.service);
    const objects: Object3D[] = [];
    const crossings = findCrossings(rails, roads).map((c): Placed => {
      const lamps: [MeshStandardMaterial, MeshStandardMaterial] = [lampMaterial(), lampMaterial()];
      const booms: Group[] = [];
      // Each way in: the barrier on the right of the lane coming toward the tracks.
      for (const way of [1, -1]) {
        const ux = c.roadX * way, uz = c.roadZ * way;
        const rx = -uz, rz = ux;
        const back = c.reach + SET_BACK;
        const px = c.x - ux * back + rx * (c.halfWidth + 0.6), pz = c.z - uz * back + rz * (c.halfWidth + 0.6);
        const post = new Group();
        post.position.set(px, c.y, pz);
        // Facing the way the traffic comes from.
        post.rotation.y = Math.atan2(ux, uz);
        post.add(at(new Mesh(geometry.post, grey), 0, 1.35, 0));
        post.add(at(new Mesh(geometry.head, dark), 0, 2.2, 0.05));
        for (const [i, x] of [[0, -0.14], [1, 0.14]] as const) post.add(at(new Mesh(geometry.lamp, lamps[i]), x, 2.2, -0.05));
        // The cross: two white boards with red edges, crossed.
        for (const angle of [0.6, -0.6]) {
          const board = at(new Mesh(geometry.bar, white), 0, 2.75, -0.02);
          board.rotation.z = angle;
          post.add(board);
          const edge = at(new Mesh(geometry.bar, red), 0, 2.75, 0.0);
          edge.scale.set(1.04, 1.3, 0.5);
          edge.rotation.z = angle;
          post.add(edge);
        }
        post.add(at(new Mesh(geometry.box, grey), 0.45, 0.55, 0.35));
        // The boom, pivoting at the post, reaching over the lane to the middle of the road.
        const pivot = new Group();
        pivot.position.set(px - rx * 0.1, c.y + 1.0, pz - rz * 0.1);
        pivot.rotation.y = Math.atan2(rz, -rx);
        const boom = new Group();
        const length = c.halfWidth + 0.4;
        const pieces = Math.max(3, Math.round(length / 0.9));
        for (let k = 0; k < pieces; k++) {
          const piece = at(new Mesh(geometry.boomPiece, k % 2 === 0 ? red : white), (k + 0.5) * (length / pieces), 0, 0);
          piece.scale.x = length / pieces;
          piece.castShadow = true;
          boom.add(piece);
        }
        boom.add(at(new Mesh(geometry.counter, dark), -0.3, 0, 0));
        pivot.add(boom);
        boom.rotation.z = UP;
        group.add(post, pivot);
        objects.push(post, pivot);
        booms.push(boom);
      }
      return { crossing: c, state: openCrossing(), booms, lamps, ring: 0 };
    });
    if (crossings.length > 0) this.chunks.set(id, { group, crossings, objects });
  }

  remove(id: string): void {
    const chunk = this.chunks.get(id);
    if (chunk === undefined) return;
    chunk.group.remove(...chunk.objects);
    for (const p of chunk.crossings) for (const m of p.lamps) m.dispose();
    this.chunks.delete(id);
  }

  get count(): number {
    let n = 0;
    for (const c of this.chunks.values()) n += c.crossings.length;
    return n;
  }

  /**
   * Each frame: the trains about (scene metres), the player at (px, pz) to
   * hear the bells from, and the sound.
   */
  update(dt: number, trains: readonly TrainView[], px: number, pz: number, sound: SoundSystem | undefined): void {
    this.clock += dt;
    const flash = Math.floor(this.clock * FLASH * 2) % 2;
    const ringing: Array<{ p: Placed; x: number; y: number; z: number; d: number }> = [];
    for (const { group, crossings } of this.chunks.values()) {
      for (const p of crossings) {
        const c = p.crossing;
        const x = c.x + group.position.x, z = c.z + group.position.z;
        let train = false;
        for (const t of trains) {
          if (coming(x, z, c.reach, t.x, t.z, t.dx, t.dz, t.speed, t.cars)) { train = true; break; }
        }
        stepCrossing(p.state, dt, train);
        for (const b of p.booms) b.rotation.z = UP * (1 - p.state.boom);
        const on = signalling(p.state);
        p.lamps[0].emissiveIntensity = on && flash === 0 ? 2.2 : 0;
        p.lamps[1].emissiveIntensity = on && flash === 1 ? 2.2 : 0;
        if (on) ringing.push({ p, x, y: c.y + group.position.y + 2.2, z, d: Math.hypot(x - px, z - pz) });
      }
    }
    // The bell, from the nearest few ringing.
    ringing.sort((a, b) => a.d - b.d);
    for (const r of ringing.slice(0, BELLS)) {
      r.p.ring -= dt;
      if (r.p.ring > 0 || r.d > BELL_HEARD) continue;
      r.p.ring = RING_EVERY;
      sound?.play('crossing:bell', { bus: 'world', at: { x: r.x, y: r.y, z: r.z }, gain: 0.8, near: 12 });
    }
  }

  /** Each lowered boom as something standing in its lane: the cars wait at it. */
  movers(): Mover[] {
    const out: Mover[] = [];
    for (const { group, crossings } of this.chunks.values()) {
      for (const p of crossings) {
        if (!barring(p.state)) continue;
        const c = p.crossing;
        for (const way of [1, -1]) {
          const ux = c.roadX * way, uz = c.roadZ * way, rx = -uz, rz = ux;
          const back = c.reach + SET_BACK;
          // Over the lane coming in, the right half of the road: the way out is left clear.
          out.push({
            x: c.x + group.position.x - ux * back + rx * c.halfWidth / 2, y: c.y + group.position.y,
            z: c.z + group.position.z - uz * back + rz * c.halfWidth / 2,
            dx: ux, dz: uz, halfLength: 0.6, halfWidth: c.halfWidth / 2 + 0.3, speed: 0,
          });
        }
      }
    }
    return out;
  }
}

function lampMaterial(): MeshStandardMaterial {
  return new MeshStandardMaterial({ color: 0x3a0c0a, emissive: RED, emissiveIntensity: 0, roughness: 0.4, flatShading: true });
}

function at<T extends Object3D>(o: T, x: number, y: number, z: number): T {
  o.position.set(x, y, z);
  return o;
}

/** The centrelines of the ribbons of the styles `keep` takes: each run of quads, its middles, with its half width. */
function centrelines(positions: Float32Array, indices: Uint32Array, attributes: Float32Array, keep: (style: number) => boolean) {
  const out: Array<{ points: number[]; halfWidth: number }> = [];
  let run: { points: number[]; halfWidth: number } | undefined;
  let previous = -10;
  const quads = Math.floor(indices.length / 6);
  const mid = (v: number) => [(positions[v * 3] + positions[(v + 1) * 3]) / 2, (positions[v * 3 + 1] + positions[(v + 1) * 3 + 1]) / 2, (positions[v * 3 + 2] + positions[(v + 1) * 3 + 2]) / 2];
  const half = (v: number) => Math.hypot(positions[v * 3] - positions[(v + 1) * 3], positions[v * 3 + 2] - positions[(v + 1) * 3 + 2]) / 2;
  for (let q = 0; q < quads; q++) {
    const a = indices[q * 6];
    if (!keep(attributes[a * 4 + 2])) { previous = -10; run = undefined; continue; }
    if (a !== previous + 2 || run === undefined) {
      run = { points: [...mid(a)], halfWidth: half(a) };
      out.push(run);
    }
    run.points.push(...mid(a + 2));
    previous = a;
  }
  return out.filter(r => r.points.length >= 6);
}
